import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  ActivityBlock,
  ActivityGlyph,
  AssistantProse,
  AttachmentCard,
  Composer,
  ComposerAttachmentTray,
  Caret,
  ConversationColumn,
  ConversationTurn,
  MessageAttachmentRow,
  UserMessage,
} from '~/design-system/components/v1_5/Chat';
import type { ComposerTextRowHeight } from '~/design-system/components/v1_5/Chat';
import { KitChip, kitThemeClass } from '~/design-system/components/v1_5/BrandKits';
import { DeckMark } from '~/design-system/components/v1_5/Header/DeckMark';
import { PanelHeader } from '~/design-system/components/v1_5/Layout';
import { cn } from '~/lib/utils';
import { GRANOLA, GRANOLA_COLORS, GRANOLA_IMAGES, GRANOLA_TYPE } from './granolaKit';
import { GranolaSlidePlate } from './GranolaSlideArt';
import type { GranolaSlideKind } from './GranolaSlideArt';
import { useBeat, useInView, usePrefersReducedMotion } from './useDemoBeat';
import styles from './BrandKitDemo.module.css';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';

/**
 * Feature row 4 — "Your brand, already applied" (Beka, 2026-09-12).
 *
 * TWO PANES, like row 1: the conversation on the left, what it made on the right. What it made is a
 * BENTO — the mark, the site it read, that site's own pictures, the palette, the two faces and the
 * deck that comes out of them — and every one of those is on the plate AT ONCE rather than taking
 * turns. A kit is a thing you look at whole; showing it a tile at a time made it read as a list.
 *
 * Four passes got here, and each one was a correction rather than a redesign:
 *
 *   1. the real kit page — five product panels down a scrolling column over seventeen seconds.
 *      Too much to take in from a row you are scrolling past.
 *   2. a URL field and three tiles. Legible, but it said a kit comes from a link box. It does not:
 *      it comes from ASKING for one, with whatever you have to hand attached.
 *   3. the ask, plus the product's own four-tile quilt — squeezed to whatever slice of plate was
 *      left, which turned 218x200 tiles into 117x200 ones and stood the website outside the panel.
 *   4. this: the bento is laid out once at the size its contents want, the app window around it is
 *      1180 wide exactly as in rows 1-3, and the panel crops it. Nothing is measured against the
 *      band the plate happens to show.
 *
 * The brand is granola.ai, real, off the same capture `FeaturesSection/WebImportDemo` already ships
 * on this page — see `granolaKit.ts` for why that stopped being an invented one.
 */

const THEME = 'dark' as const;
const MODEL = { vendor: 'google', label: 'Gemini 3.8 Flash' } as const;

/*
 * The ask, typed straight.
 *
 * The site is IN THE SENTENCE and is just a word in it — it resolved into a `MentionChip` for a
 * while, and a chip is the app interrupting a sentence to say it recognised part of it (Beka,
 * 2026-09-12). You write where your brand lives; the agent works out that it is a URL. Only the PDF
 * is an attachment, because a file is a thing you hand over and an address is a thing you say.
 */
const ASK = `Pull our brand from ${GRANOLA.host} and this guide into a kit.`;

/**
 * The composer grows with the sentence, and this is how it knows to.
 *
 * `ComposerTextRow`'s height is a FIXED prop out of a five-value union, and a `flow` row also clips
 * — so a caller that leaves it at the 54px default gets one line and loses the rest, which is
 * exactly what this row was doing: everything after "Make a " was drawn and then cut off (Beka,
 * 2026-09-12). The heights are not content-derived by design (the 14px of slack under the last line
 * is part of the box), so growing is the caller's job.
 *
 * Measured rather than counted: the ask wraps where the font and the chip's width say it does, at a
 * width the composer owns.
 */
const ASK_HEIGHTS: readonly ComposerTextRowHeight[] = [54, 78, 102, 126, 174];
/** The row's top padding. Everything below it is line boxes and the trailing slack. */
const TEXT_ROW_PAD = 16;

const BEATS = ['ask', 'sent', 'read', 'kit'] as const;
type Beat = (typeof BEATS)[number];

/**
 * THE CONVERSATION LEAVES BEFORE THE KIT ARRIVES, and that is a layout decision as much as a
 * narrative one.
 *
 * The agent pane holds 344 of the 736 the plate shows, so anything laid out to its right while it is
 * open starts half off the edge. Letting it go first means the bento is only ever seen with the
 * whole plate behind it — the kit lands into the room the conversation just left.
 */
const DWELL: Record<Beat, number> = {
  ask: 3400,
  sent: 1800,
  read: 2800,
  kit: 6400,
};

/**
 * ONE ARRIVAL, CASCADED — every cell lands inside the same beat, each a step behind the last.
 *
 * Two things were asked of this, and they are compatible. The artifacts used to arrive on a beat of
 * their own after the rest, which made the kit look like it was still working after it had finished
 * (Beka, 2026-09-12: show everything at once) — so there is still exactly one moment the bento
 * appears. Then a flat simultaneous pop was too dead for the one moment the row is about (same day:
 * small delays, more interesting) — so inside that moment the cells come in a sweep.
 *
 * The whole cascade is under a second: 180ms of the pane's 560ms exit before the first, seven 70ms
 * steps, and the last one's own 280ms. Long enough to see the order, short enough that nobody is
 * still waiting for the kit.
 */
const KIT_DELAY_MS = 180;
const STAGGER_MS = 70;

/**
 * The order the bento fills in: a diagonal sweep from the top-left, one cell per step.
 *
 * Diagonal rather than reading order because a grid filled row by row reads as a table being
 * populated, and a wave that crosses it reads as one thing arriving. Cells on the same diagonal are
 * broken by row, so nothing lands together and every step is a single visible event. The mark is in
 * the top-left corner, so it lands first and the site it was read from lands second. The artifacts
 * are the last three slots, left to right — the kit first, then what it made.
 */
const STAGGER = { logo: 0, site: 1, type: 2, images: 3, colors: 4, slides: 5 } as const;

/** Per character. `WebImportDemo` types at 28ms; a sentence this long wants to be quicker. */
const TYPE_MS = 26;

/** The bento's own gutter, from the stylesheet. Three artifact plates share what is left over. */
const BENTO_GAP = 12;

/** The deck the kit made, in reading order. The drawings are `GranolaSlideArt`'s. */
const SLIDES: readonly GranolaSlideKind[] = ['cover', 'points', 'figure'];

/**
 * The guide's page behind its chip: `AttachmentCard` is a fixed 96px square, and the page is drawn at
 * the square's height, so it is 16:9 of 96 wide and the chip shows its left edge. The stylesheet's
 * `.guideArt` holds the same number.
 */
const CHIP_PX = 96;
const GUIDE_ART_W = (CHIP_PX * 16) / 9;

export interface BrandKitDemoProps {
  className?: string;
}

export function BrandKitDemo({ className }: BrandKitDemoProps) {
  const [hostRef, inView] = useInView<HTMLDivElement>();
  const reduceMotion = usePrefersReducedMotion();
  const beat = useBeat(BEATS, DWELL, inView && !reduceMotion);
  const at = (name: Beat) => BEATS.indexOf(beat) >= BEATS.indexOf(name);

  const sent = at('sent');
  const reading = beat === 'sent';
  /* The agent has said its piece and steps out, leaving the plate to the bento. */
  const alone = at('kit');

  /* The ask arrives a character at a time, and leaves the composer the moment it is sent. */
  const [typed, setTyped] = useState(ASK.length);
  useEffect(() => {
    if (beat !== 'ask') {
      setTyped(ASK.length);
      return;
    }
    setTyped(0);
    const step = window.setInterval(() => setTyped((n) => Math.min(ASK.length, n + 1)), TYPE_MS);
    return () => window.clearInterval(step);
  }, [beat]);

  /*
   * THE COMPOSER'S HEIGHT, off the sentence it is holding.
   *
   * The flow is wrapped in a box this row owns and hands to `Composer` as its children, so what is
   * measured is the real thing in its real place — the product's own type, at the width the card
   * gives it, with the real `MentionChip` in the line. The smallest step whose content box clears it
   * wins; nothing is ever cut, and a step is never taller than the sentence needs.
   */
  const askRef = useRef<HTMLSpanElement>(null);
  const [askHeight, setAskHeight] = useState<ComposerTextRowHeight>(54);
  useIsomorphicLayoutEffect(() => {
    const node = askRef.current;
    if (!node) return;
    const measure = () => {
      const wanted = node.offsetHeight;
      setAskHeight(
        ASK_HEIGHTS.find((h) => h - TEXT_ROW_PAD >= wanted) ?? ASK_HEIGHTS[ASK_HEIGHTS.length - 1],
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [sent]);

  /*
   * THE ARTIFACT PLATES, off the bento's own width.
   *
   * `SlideThumb` takes a pixel width and scales the 1280-wide artifact to it, so the three plates
   * have to be told what a third of the row comes to. Measured rather than restated, because the
   * bento has two widths (800, and 664 below the 1280 breakpoint) and the plates keep 16:9 — which
   * is what makes the grid's third row size itself.
   */
  const bentoRef = useRef<HTMLDivElement>(null);
  const [slideWidth, setSlideWidth] = useState(258);
  useIsomorphicLayoutEffect(() => {
    const node = bentoRef.current;
    if (!node) return;
    /* Less the cell's own two hairlines: the plate fills the cell's CONTENT box, and a scale taken
       from the outer width draws the artifact two pixels wider than the box that clips it. */
    const measure = () =>
      setSlideWidth(Math.max(120, Math.floor((node.clientWidth - BENTO_GAP * 2) / 3) - 2));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /*
   * The one attachment, drawn the same way in the composer's tray and on the sent message.
   *
   * With granola's brand guide behind it (Beka, 2026-09-15): a chip that only said `PDF` gave the
   * reader nothing to recognise, and the guide's first page says what the file is before its name
   * does. The page is this row's and not the chip's — `AttachmentCard` stays the product's, which
   * draws no preview on a titled chip — so the chip sits over it. See `.guide`.
   *
   * `theme="light"` on this one chip, and that is what keeps the page clean: its type is the chip's
   * dark set, which reads on the lime as it stands, where the dark theme's white would have needed
   * something laid over the page to read at all.
   */
  const sources = (
    <span className={styles.guide}>
      <span className={styles.guideArt}>
        <GranolaSlidePlate kind="guide" width={GUIDE_ART_W} />
      </span>
      <AttachmentCard variant="titled" kind="PDF" name="brand-guide.pdf" theme="light" />
    </span>
  );

  /** The ask as the composer holds it: the sentence so far, and the caret. */
  const askFlow = (
    <span ref={askRef} className={styles.askFlow}>
      {ASK.slice(0, typed)}
      <span className={styles.askCaret}>
        <Caret theme={THEME} />
      </span>
    </span>
  );

  /** A cell's slot in the cascade, handed to the stylesheet as a runtime value. */
  const slot = (index: number) =>
    ({ '--cell-delay': `${KIT_DELAY_MS + index * STAGGER_MS}ms` }) as CSSProperties;

  return (
    <div ref={hostRef} className={cn(styles.window, className)} aria-hidden>
      <div className={styles.bar}>
        <DeckMark theme={THEME} hover="inert" />
        <span className={styles.barTitle}>Deck, {GRANOLA.name}</span>
      </div>

      <div className={cn(styles.panes, alone && styles.panesGone)}>
        {/* ── the conversation ──────────────────────────────────────────────────────────────── */}
        <div className={styles.agent}>
          <PanelHeader theme={THEME}>Agent</PanelHeader>

          {/* Bottom-aligned, as row 1's is: a thread grows upward out of the composer. */}
          <div className={styles.thread}>
            {sent ? (
              <ConversationColumn theme={THEME}>
                <ConversationTurn
                  theme={THEME}
                  /* The only turn in this column, so it pays no `.spaced` top margin — the component
                     says the caller is the one who knows which turn is first, and row 1 says the
                     same. Those 16px are the difference between the message fitting and not. */
                  spaced={false}
                  user={
                    <UserMessage
                      theme={THEME}
                      attachments={
                        <MessageAttachmentRow theme={THEME}>{sources}</MessageAttachmentRow>
                      }
                    >
                      {ASK}
                    </UserMessage>
                  }
                >
                  <ActivityBlock
                    theme={THEME}
                    row={{
                      density: 'collapsed',
                      disclosure: 'down',
                      icon: (
                        <ActivityGlyph
                          state={reading ? 'running' : 'done'}
                          size={15}
                          tone="hint"
                          theme={THEME}
                        />
                      ),
                      label: reading ? 'Reading 2 sources' : 'Read 2 sources',
                      meta: `${GRANOLA.host} · brand-guide.pdf`,
                      theme: THEME,
                    }}
                  />
                  {/* Both lines land on `read`, and neither waits for a beat the pane will not be
                      open for: the agent leaves on the next one. */}
                  {at('read') ? (
                    <AssistantProse theme={THEME}>
                      The mark is one plated glyph, and the site and the guide agree on the palette —
                      five colours, with the lime as the accent.
                    </AssistantProse>
                  ) : null}
                  {at('read') ? (
                    <AssistantProse theme={THEME}>
                      Quadrant over Melange, off the stylesheet. Every deck you make now starts here.
                    </AssistantProse>
                  ) : null}
                </ConversationTurn>
              </ConversationColumn>
            ) : null}
          </div>

          <div className={styles.dock}>
            <Composer
              theme={THEME}
              width={320}
              attachments={
                sent ? undefined : (
                  <ComposerAttachmentTray theme={THEME}>{sources}</ComposerAttachmentTray>
                )
              }
              input={
                sent
                  ? { content: 'placeholder', text: 'Ask for a change…' }
                  : { content: 'flow', children: askFlow, height: askHeight }
              }
              toolbar={{ model: MODEL, send: { state: reading ? 'stop' : sent ? 'idle' : 'active' } }}
            />
          </div>
        </div>

        {/* ── what it made ──────────────────────────────────────────────────────────────────── */}
        <div className={styles.result}>
          <PanelHeader theme={THEME}>Brand kit</PanelHeader>

          <div className={styles.stage}>
            {/* `kitThemeClass` brings BOTH of the product's layers — the header's `--hv15-*` ramp and
                the kit's own `--kv15-*` grounds — so every tile below is painted out of the same
                vocabulary the real kit page uses rather than out of look-alike literals. */}
            <div ref={bentoRef} className={cn(kitThemeClass(THEME), styles.bento)}>
              {at('kit') ? (
                <>
                  <div className={cn(styles.cell, styles.logo)} style={slot(STAGGER.logo)}>
                    <KitChip theme={THEME} className={styles.chip}>
                      Logo
                    </KitChip>
                    <img className={styles.logoMark} src={GRANOLA.logo} alt="" decoding="async" />
                  </div>

                  {/* No chip and no host line: the picture says both, and a tag over it was the app
                      captioning a screenshot of a home page. */}
                  <div className={cn(styles.cell, styles.site)} style={slot(STAGGER.site)}>
                    <img className={styles.siteShot} src={GRANOLA.screenshot} alt="" decoding="async" />
                  </div>

                  {/* `tone="scrim"` on the three that are drawn over the brand's own pixels: the
                      plate under those chips is the user's colour and does not follow our theme. */}
                  <div className={cn(styles.cell, styles.images)} style={slot(STAGGER.images)}>
                    <KitChip theme={THEME} tone="scrim" className={styles.chip}>
                      Images
                    </KitChip>
                    {GRANOLA_IMAGES.map((image) => (
                      <img key={image.src} src={image.src} alt={image.alt} decoding="async" />
                    ))}
                  </div>

                  <div className={cn(styles.cell, styles.type)} style={slot(STAGGER.type)}>
                    <KitChip theme={THEME} className={styles.chip}>
                      Typography
                    </KitChip>
                    <span className={styles.faceDisplay}>{GRANOLA_TYPE.display}</span>
                    <span className={styles.faceBody}>{GRANOLA_TYPE.body}</span>
                  </div>

                  <div className={cn(styles.cell, styles.colors)} style={slot(STAGGER.colors)}>
                    <KitChip theme={THEME} tone="scrim" className={styles.chip}>
                      Colors
                    </KitChip>
                    <div className={styles.colorsBody}>
                      {GRANOLA_COLORS.map((color) => (
                        <span
                          key={color.role}
                          className={styles.block}
                          style={{ '--kit-block': color.value } as CSSProperties}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.slides}>
                    {SLIDES.map((kind, i) => (
                      <div key={kind} className={styles.cell} style={slot(STAGGER.slides + i)}>
                        <GranolaSlidePlate
                          className={styles.slidePlate}
                          kind={kind}
                          width={slideWidth}
                        />
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

BrandKitDemo.displayName = 'BrandKitDemo';
